import { useState } from "react";
import type { ColumnDef, EditableRow as EditableRowType } from "../types";
import { EditableCell } from "./EditableCell";

interface Props {
  row: EditableRowType;
  columns: ColumnDef[];
  onUpdate: (next: EditableRowType) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  /** True if this row is rendered as a child of another row. */
  isChild?: boolean;
  /** Left offset (px) at which the first column pins during horizontal scroll. */
  firstColumnLeft: number;
  /**
   * Position of this row in the workstream, shown as its priority rank. Counts
   * the underlying order, not the filtered view, so "the top 5" keeps meaning
   * the same thing when a filter is on. Sub-rows aren't ranked.
   */
  rank?: number;
}

export function EditableRow({
  row,
  columns,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  isChild,
  firstColumnLeft,
  rank,
}: Props) {
  const children = row.children ?? [];
  const hasChildren = children.length > 0;
  const [expanded, setExpanded] = useState(true);

  function updateCell(colId: string, value: string) {
    onUpdate({
      ...row,
      cells: { ...row.cells, [colId]: value },
    });
  }

  function updateChild(updated: EditableRowType) {
    onUpdate({
      ...row,
      children: children.map((c) => (c.id === updated.id ? updated : c)),
    });
  }

  function deleteChild(id: string) {
    const next = children.filter((c) => c.id !== id);
    onUpdate({ ...row, children: next.length > 0 ? next : undefined });
  }

  function moveChild(idx: number, dir: -1 | 1) {
    const next = [...children];
    const ni = idx + dir;
    if (ni < 0 || ni >= next.length) return;
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onUpdate({ ...row, children: next });
  }

  // The first column carries the expand/collapse chevron for rows that still
  // have sub-rows. New sub-rows can no longer be created (per HSS, June 2026),
  // but existing ones stay editable and deletable.
  const firstCol = columns[0];
  const restCols = columns.slice(1);

  // Pinned cells must paint their own background, including on hover, or the
  // columns scrolling underneath show through them.
  const pinnedBg = isChild
    ? "bg-slate-50 group-hover:bg-slate-100"
    : "bg-white group-hover:bg-slate-50";

  return (
    <>
      {/* Row shades are opaque so the pinned cells, which must paint their own
          background, match the rest of the row exactly. */}
      <tr
        className={`group transition-colors border-b border-slate-100 ${
          isChild ? "bg-slate-50 hover:bg-slate-100" : "hover:bg-slate-50"
        }`}
      >
        <td className={`sticky left-0 z-10 px-2 py-2.5 align-top ${pinnedBg}`}>
          <div className="flex items-center gap-0.5">
            <span className="w-5 shrink-0 text-[11px] tabular-nums text-slate-400 text-right">
              {rank ?? ""}
            </span>
            <button
              onClick={onMoveUp}
              disabled={isFirst}
              className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20 text-xs"
            >
              &#9650;
            </button>
            <button
              onClick={onMoveDown}
              disabled={isLast}
              className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20 text-xs"
            >
              &#9660;
            </button>
          </div>
        </td>
        {/* This cell carries no min-width: a sticky cell wider than its column
            paints over the next one. Width comes from the table's colgroup. */}
        {firstCol && (
          <td
            className={`sticky z-10 px-2 py-2.5 align-top border-r border-slate-200 ${pinnedBg} ${
              isChild ? "pl-8" : ""
            }`}
            style={{ left: firstColumnLeft }}
          >
            <div className="flex items-start gap-1.5">
              {hasChildren && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-1 text-slate-400 hover:text-slate-700 text-[10px] shrink-0"
                  aria-label={expanded ? "Collapse" : "Expand"}
                >
                  {expanded ? "\u25BC" : "\u25B6"}
                </button>
              )}
              <div className="flex-1 min-w-0">
                <EditableCell
                  column={firstCol}
                  value={row.cells[firstCol.id] ?? ""}
                  onChange={(v) => updateCell(firstCol.id, v)}
                />
              </div>
            </div>
          </td>
        )}
        {restCols.map((col) => (
          <td
            key={col.id}
            className={`px-2 py-2.5 align-top ${
              col.type === "longText" ? "min-w-[150px]" : "min-w-[110px]"
            } ${col.type === "status" || col.type === "badge" ? "whitespace-nowrap" : ""}`}
          >
            <EditableCell
              column={col}
              value={row.cells[col.id] ?? ""}
              onChange={(v) => updateCell(col.id, v)}
            />
          </td>
        ))}
        <td className="px-2 py-2.5 w-8 align-top">
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-sm transition-opacity"
          >
            &times;
          </button>
        </td>
      </tr>
      {hasChildren &&
        expanded &&
        children.map((child, ci) => (
          <EditableRow
            key={child.id}
            row={child}
            columns={columns}
            onUpdate={updateChild}
            onDelete={() => deleteChild(child.id)}
            onMoveUp={() => moveChild(ci, -1)}
            onMoveDown={() => moveChild(ci, 1)}
            isFirst={ci === 0}
            isLast={ci === children.length - 1}
            isChild
            firstColumnLeft={firstColumnLeft}
          />
        ))}
    </>
  );
}
